import React from 'react';
import styles from './UTMContent.module.css';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';

const URLStructure = () => {
  return (
    <Container style={{ padding: '0px 25px 20px' }}>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', margin: '16px 0', flexWrap: 'wrap' }}>
        <span style={{ marginRight: '8px', fontWeight: 'bold' }}>Form's URL structure:</span>
        <Chip label="Landing Page URL" variant="outlined" style={{ backgroundColor: '#e0f7fa', marginRight: '8px' }} />
        <span>?</span>
        <Chip label="cid=" variant="outlined" style={{ backgroundColor: '#a5d6a7', margin: '0 8px' }} />
        <span>:</span>
        <Chip label="source" variant="outlined" style={{ backgroundColor: '#ffe0b2', margin: '0 8px' }} />
        <span>:</span>
        <Chip label="medium" variant="outlined" style={{ backgroundColor: '#e1bee7', margin: '0 8px' }} />
        <span>:</span>
        <Chip label="objective" variant="outlined" style={{ backgroundColor: '#ffabab', margin: '0 8px' }} />
        <span>:</span>
        <Chip label="campaign_name" variant="outlined" style={{ backgroundColor: '#c8e6c9', margin: '0 8px' }} />
        <span>:</span>
        <Chip label="adset name" variant="outlined" style={{ backgroundColor: '#ffccbc', margin: '0 8px' }} />
        <span>:</span>
        <Chip label="ad name" variant="outlined" style={{ backgroundColor: '#bbdefb' }} />
        <span>:</span>
        <Chip label="date" variant="outlined" style={{ backgroundColor: '#bbdefb' }} />
      </div>
    </Container>
  );
};

const UTMContent = () => {
  return (
    <section className={styles.container}>
      <header className={styles.header}>
        <h2 className={styles.title}>Adobe Campaign CID Builder (s.campaign) Parameters Explained</h2>
        <p className={styles.subtitle}>
            This table explains each part of the <strong>Adobe Analytics CID tracking code</strong> (the <strong>s.campaign</strong> variable), including examples and best practices. 
            Use it to standardize your campaign tracking links and reduce reporting cleanup in{' '}
          <a href="https://business.adobe.com/products/analytics/adobe-analytics.html" target="_blank" rel="noopener noreferrer" className={styles.link}>
            Adobe Analytics
          </a>
          .
        </p>
        {/* short “admin settings” clarification (keeps this page unique + reduces confusion) */}
<p className={styles.subtitle}>
  Quick note: this builder outputs <code className={styles.inlineCode}>cid=</code> by default. Some organizations use <code className={styles.inlineCode}>ccid</code>, <code className={styles.inlineCode}>pcid</code>, or another parameter mapped to Adobe Tracking Code, so confirm your standard first if your team uses a different setup.
</p>
      </header>

      <URLStructure />

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>Parameter</th>
            <th className={styles.th}>Required</th>
            <th className={styles.th}>Example</th>
            <th className={styles.th}>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr className={styles.tr}>
            <td className={styles.td}>
              s.campaign variable
              <div>
                <Chip label="cid=" variant="outlined" style={{ backgroundColor: '#a5d6a7' }} />
              </div>
            </td>
            <td className={`${styles.td} ${styles.required}`}>Yes</td>
            <td className={styles.td}>cid, ccid, pcid</td>
            <td className={styles.td}>
              This form generates a <strong>cid=</strong> tracking code, which many organizations map to Adobe Analytics <strong>s.campaign</strong>.
              Some teams use variants like <strong>ccid</strong> or <strong>pcid</strong>. The key is consistency so reporting and classifications stay clean.
            </td>
          </tr>
          <tr className={styles.tr}>
            <td className={styles.td}>
              Campaign Source
              <div>
                <Chip label="source" variant="outlined" style={{ backgroundColor: '#ffe0b2' }} />
              </div>
            </td>
            <td className={`${styles.td} ${styles.required}`}>Yes</td>
            <td className={styles.td}>google, facebook, instagram</td>
            <td className={styles.td}>Where your traffic is coming from.</td>
          </tr>
          <tr className={styles.tr}>
            <td className={styles.td}>
              Campaign Medium
              <div>
                <Chip label="medium" variant="outlined" style={{ backgroundColor: '#e1bee7' }} />
              </div>
            </td>
            <td className={`${styles.td} ${styles.required}`}>Yes</td>
            <td className={styles.td}>cpc, banner, display</td>
            <td className={styles.td}>What type of medium the link is used upon.</td>
          </tr>
          <tr className={styles.tr}>
            <td className={styles.td}>
              Campaign Objective
              <div>
                <Chip label="objective" variant="outlined" style={{ backgroundColor: '#ffabab' }} />
              </div>
            </td>
            <td className={`${styles.td} ${styles.required}`}>Yes</td>
            <td className={styles.td}>awareness (aw), consideration (co), conversion (ko)</td>
            <td className={styles.td}>Identify the category behind the URL.</td>
          </tr>
          <tr className={styles.tr}>
            <td className={styles.td}>
              Campaign
              <div>
                <Chip label="campaign_name" variant="outlined" style={{ backgroundColor: '#c8e6c9' }} />
              </div>
            </td>
            <td className={`${styles.td} ${styles.required}`}>Yes</td>
            <td className={styles.td}>holiday_sale, summer, new_release</td>
            <td className={styles.td}>The specific promotion or strategic campaign for the URL.</td>
          </tr>
          <tr className={styles.tr}>
            <td className={styles.td}>
              Ad Set Name
              <div>
                <Chip label="adset name" variant="outlined" style={{ backgroundColor: '#ffccbc' }} />
              </div>
            </td>
            <td className={styles.td}>No</td>
            <td className={styles.td}>mens_shoes, genz</td>
            <td className={styles.td}>A sub-group or variant within a campaign.</td>
          </tr>
          <tr className={styles.tr}>
            <td className={styles.td}>
              Ad Name
              <div>
                <Chip label="ad name" variant="outlined" style={{ backgroundColor: '#bbdefb' }} />
              </div>
            </td>
            <td className={styles.td}>No</td>
            <td className={styles.td}>blue, family_picture</td>
            <td className={styles.td}>Use clear, descriptive terms for the unique name.</td>
          </tr>
          <tr className={styles.tr}>
            <td className={styles.td}>
              Date
              <div>
                <Chip label="date" variant="outlined" style={{ backgroundColor: '#bbdefb' }} />
              </div>
            </td>
            <td className={styles.td}>No</td>
            <td className={styles.td}>01, january</td>
            <td className={styles.td}>Used to group campaign links by initial publication month.</td>
          </tr>
        </tbody>
      </table>
      <p className={styles.subtitle}>
        Need help with Adobe Analytics setup? Read{' '}
        <a href="https://utmsmartmanager.com/the-complete-guide-to-using-adobe-analytics-utm-parameters/" target="_blank" rel="noopener noreferrer" className={styles.link}>
          The Complete Guide to Using Adobe Analytics UTM Parameters
        </a>{' '}
        (also known as Adobe CID link parameters).
      </p>
      <p className={styles.caption}>
        About{' '}
        <a
          href="https://utmsmartmanager.com/adobe-analytics/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          UTM Smart Manager for Adobe Analytics:
        </a>{' '}
        start with the free Adobe CID builder, then see how the{' '}
        <a
          href="https://utmsmartmanager.com/adobe-analytics/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          Adobe Analytics solution
        </a>{' '}
        uses{' '}
        <a
          href="https://utmsmartmanager.com/campaign-url-studio/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          Campaign URL Studio
        </a>{' '}
        to give teams shared rules and a governed workflow. When you are ready to personalize your own Adobe campaign URL builder, that is the next step. It works with <code className={styles.inlineCode}>cid</code>, <code className={styles.inlineCode}>ccid</code>, or the parameter your org maps to Adobe Tracking Code. Want to know which creative assets performed best? Explore{' '}
        <a
          href="https://utmsmartmanager.com/assets-studio/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          Assets Studio.
        </a>
      </p>
    </section>
  );
};

export default UTMContent;
